import { Info } from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { getTerm } from "@/lib/glossary";

/**
 * <InfoTip term="ASIN" />
 * Renders a 14×14 info icon next to a label. Hover/focus reveals the
 * glossary entry.
 *
 * Optional props:
 *   - text:  override the body
 *   - title: override the header (defaults to glossary `full`)
 *   - side:  Radix tooltip side — "top" | "right" | "bottom" | "left"
 */
export function InfoTip({ term, title, text, side = "top", className = "" }) {
  const entry = getTerm(term);
  const heading = title || entry?.full;
  const body = text || entry?.blurb;
  if (!heading && !body) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            tabIndex={0}
            aria-label={heading || "More info"}
            data-testid={`infotip-${(term || "x").toLowerCase()}`}
            className={`inline-flex items-center justify-center align-middle text-slate-400 hover:text-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded-full ${className}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            <Info className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          className="max-w-[280px] bg-slate-900 text-slate-100 px-3 py-2 text-xs leading-snug shadow-xl"
        >
          {heading && <div className="font-semibold text-white mb-0.5">{heading}</div>}
          {body && <div className="text-slate-300">{body}</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * <GlossaryTerm term="ASIN">ASIN</GlossaryTerm>
 * Renders the children with a dotted underline; hover/focus shows the same
 * glossary entry as <InfoTip>. Useful for inline mention of an acronym.
 */
export function GlossaryTerm({ term, children, className = "" }) {
  const entry = getTerm(term);
  if (!entry) return <>{children}</>;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label={entry.full}
            data-testid={`glossary-${term.toLowerCase()}`}
            className={`underline decoration-dotted decoration-slate-400 underline-offset-2 cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded-sm ${className}`}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[280px] bg-slate-900 text-slate-100 px-3 py-2 text-xs leading-snug shadow-xl"
        >
          <div className="font-semibold text-white mb-0.5">{entry.full}</div>
          <div className="text-slate-300">{entry.blurb}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Convenience: <LabelWithTip term="ASIN">ASIN</LabelWithTip>
 * Combines both — visible text gets the dotted underline AND an info icon
 * sits to its right.
 */
export function LabelWithTip({ term, children, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <GlossaryTerm term={term}>{children}</GlossaryTerm>
      <InfoTip term={term} />
    </span>
  );
}
